import React from 'react';
import { PhoneNumberInput } from '../ui/PhoneNumberInput';
import { IconId, IconAlignLeft, IconLink, IconAt, IconHash, IconTextSize, IconCloudUpload } from '@tabler/icons-react';
import { ConversationIcon } from '../icons';
import type { FormField } from '../../types';

/** Answer formats represented by a text preview rather than option or rating controls. */
type PreviewField = Extract<FormField, { type: 'email' | 'phone' | 'name' | 'number' | 'short_text' | 'free_form_explore' | 'file_upload' }>;

/** Keeps the configured answer format visible even when an authored placeholder is generic. */
export function AnswerFieldPreview({ field }: { field: PreviewField }): React.ReactElement {
  const expanded = field.type === 'short_text' && field.textFormat !== 'url' && field.textPresentation === 'expanded';
  if (field.type === 'file_upload') return <div data-testid="field-card-preview" role="group" aria-label="File upload answer preview" className="mt-2 flex min-h-28 flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-border bg-background/30 px-4 py-5 text-muted-foreground">
    <IconCloudUpload aria-hidden="true" className="h-6 w-6" stroke={1.4} />
    <span className="text-sm font-medium">Upload files</span>
    <span className="text-xs">Drop here or browse</span>
  </div>;
  const presentation = field.type === 'phone'
    ? { label: 'Phone', example: '+1 (555) 555-5555', Icon: IconAt }
    : field.type === 'email'
    ? { label: 'Email', example: 'name@example.com', Icon: IconAt }
    : field.type === 'short_text' && field.textFormat === 'url'
      ? { label: 'URL', example: 'example.com', Icon: IconLink }
    : field.type === 'name' ? { label: 'Name', example: 'Full name', Icon: IconId }
    : field.type === 'number'
      ? { label: 'Number', example: 'Enter a number', Icon: IconHash }
      : field.type === 'short_text'
        ? expanded
          ? { label: 'Text', example: 'Long answer', Icon: IconAlignLeft }
          : { label: 'Text', example: 'Short answer', Icon: IconTextSize }
        : { label: 'Explore', example: "Respondent's answer", Icon: ConversationIcon };
  const { Icon } = presentation;
  return <div data-testid="field-card-preview" role="group" aria-label={`${presentation.label} answer preview`}
    className={`mt-2 flex min-w-0 items-start gap-3 rounded-lg border border-border/60 bg-background/50 px-3 py-2 text-sm text-muted-foreground ${expanded ? 'min-h-20' : ''}`}>
    {field.type === 'phone' ? <PhoneNumberInput appearance="preview" value="" onChange={() => {}} disabled placeholder={field.placeholder?.trim() ? field.placeholder : presentation.example} /> : <>
    <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
    <span className="min-w-0 flex-1 whitespace-pre-wrap [overflow-wrap:anywhere]">{field.placeholder?.trim() ? field.placeholder : presentation.example}</span></>}
    <span className="shrink-0">{presentation.label}</span>
  </div>;
}
